import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, inr, formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Package, LogOut, User, Star, RotateCcw, Upload } from "lucide-react";

const STATUS_COLOR = {
  PLACED: "bg-blue-100 text-blue-700", CONFIRMED: "bg-blue-100 text-blue-700",
  LABEL_GENERATED: "bg-violet-100 text-violet-700", PICKED_UP: "bg-amber-100 text-amber-700",
  IN_TRANSIT: "bg-amber-100 text-amber-700", OUT_FOR_DELIVERY: "bg-orange-100 text-orange-700",
  DELIVERED: "bg-emerald-100 text-emerald-700", CANCELLED: "bg-rose-100 text-rose-700",
  RETURNED: "bg-rose-100 text-rose-700", RTO: "bg-rose-100 text-rose-700",
};

export default function Account() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [reviewModal, setReviewModal] = useState(null); // {order, item}
  const [returnModal, setReturnModal] = useState(null); // order
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [reason, setReason] = useState("");

  const loadData = () => {
    api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/returns/my").then((r) => setReturns(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (!loading && !user) { navigate("/login", { state: { from: "/account" } }); return; }
    if (user) loadData();
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="h-[50vh] flex items-center justify-center text-muted-foreground">Loading...</div>;

  const returnedOrderIds = new Set(returns.filter((r) => r.status !== "REJECTED").map((r) => r.order_id));

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please upload an image"); return; }
    setBusy(true);
    try { setPhotoUrl(await uploadFile(f)); toast.success("Photo uploaded"); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const handleVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please upload a video"); return; }
    setBusy(true);
    try { setVideoUrl(await uploadFile(f)); toast.success("Unboxing video uploaded"); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const submitReview = async () => {
    if (!photoUrl) { toast.error("A product photo is mandatory"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { product_id: reviewModal.item.product_id, order_number: reviewModal.order.order_number, rating, text: reviewText, photo_url: photoUrl });
      toast.success("Thank you for your review!");
      setReviewModal(null); setPhotoUrl(""); setReviewText(""); setRating(5);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitReturn = async () => {
    if (!videoUrl) { toast.error("Unboxing video is mandatory to request a return"); return; }
    if (!reason.trim()) { toast.error("Please enter a reason"); return; }
    setBusy(true);
    try {
      await api.post("/returns", { order_id: returnModal.id, reason, video_url: videoUrl });
      toast.success("Return request submitted!");
      setReturnModal(null); setVideoUrl(""); setReason(""); loadData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-rose-950 text-amber-50 flex items-center justify-center"><User className="w-6 h-6" /></div>
          <div><h1 className="font-serif text-2xl font-bold text-rose-950">Hello, {user.name}</h1><p className="text-sm text-muted-foreground">{user.phone}</p></div>
        </div>
        <Button variant="outline" onClick={() => { logout(); navigate("/"); }} className="border-rose-950/30" data-testid="account-logout-btn"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
      </div>

      <a href="https://wa.me/919352175539?text=Hi%20Million%20Pixels%2C%20I%20need%20help%20with%20my%20order" target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl p-4 mb-6 hover:bg-[#25D366]/15 transition-colors" data-testid="account-support-chat">
        <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center"><svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24z"/></svg></div>
        <div><p className="font-medium text-sm text-rose-950">Need help? Chat with us</p><p className="text-xs text-muted-foreground">Order issues, returns or questions — our team replies on WhatsApp</p></div>
      </a>

      <h2 className="font-serif text-xl font-semibold text-rose-950 mb-4">My Orders ({orders.length})</h2>
      {orders.length === 0 ? (
        <div className="bg-white border border-rose-900/15 rounded-2xl p-12 text-center">
          <Package className="w-14 h-14 text-rose-900/20 mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">You haven't placed any orders yet.</p>
          <Link to="/shop"><Button className="bg-rose-950 rounded-full">Start Shopping</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="bg-white border border-rose-900/15 rounded-2xl p-5" data-testid={`account-order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rose-900/10 pb-3 mb-3">
                <div>
                  <p className="font-serif font-bold text-rose-950">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{(o.created_at || "").slice(0, 10)} · {o.payment_mode}</p>
                </div>
                <Badge className={`${STATUS_COLOR[o.status] || "bg-gray-100"} border-0`}>{o.status.replace(/_/g, " ")}</Badge>
              </div>
              <div className="flex gap-3 overflow-x-auto">
                {o.items.map((it, i) => (
                  <div key={i} className="flex flex-col gap-1 min-w-[150px]">
                    <div className="flex gap-2">
                      <img src={it.image} className="w-12 h-14 object-cover rounded" alt="" />
                      <div className="text-xs"><p className="font-medium leading-tight">{it.name}</p><p className="text-muted-foreground">{it.size}·Qty {it.qty}</p></div>
                    </div>
                    {o.status === "DELIVERED" && (
                      <button onClick={() => { setReviewModal({ order: o, item: it }); setPhotoUrl(""); setReviewText(""); setRating(5); }} className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5" data-testid={`account-review-${it.sku}`}><Star className="w-3 h-3" />Write review</button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-rose-900/10">
                <span className="font-semibold text-rose-950">{inr(o.total)}</span>
                <div className="flex gap-2">
                  {o.status === "DELIVERED" && !returnedOrderIds.has(o.id) && (
                    <Button size="sm" variant="outline" className="border-rose-700/40 text-rose-700 rounded-full" onClick={() => { setReturnModal(o); setVideoUrl(""); setReason(""); }} data-testid={`account-return-${o.order_number}`}><RotateCcw className="w-3.5 h-3.5 mr-1" />Return</Button>
                  )}
                  {returnedOrderIds.has(o.id) && <Badge className="bg-amber-100 text-amber-700 border-0">Return Requested</Badge>}
                  <Link to={`/track/${o.order_number}`}><Button size="sm" variant="outline" className="border-rose-950/30 rounded-full" data-testid={`account-track-${o.order_number}`}>Track</Button></Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {returns.length > 0 && (
        <>
          <h2 className="font-serif text-xl font-semibold text-rose-950 mb-4 mt-10">My Returns ({returns.length})</h2>
          <div className="space-y-3">
            {returns.map((r) => (
              <div key={r.id} className="bg-white border border-rose-900/15 rounded-xl p-4 flex items-center justify-between" data-testid={`account-return-row-${r.order_number}`}>
                <div><p className="font-medium text-rose-950">{r.order_number}</p><p className="text-xs text-muted-foreground">{r.reason}</p></div>
                <Badge className={`${r.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : r.status === "REJECTED" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"} border-0`}>{r.status}</Badge>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Review Modal */}
      <Dialog open={!!reviewModal} onOpenChange={(v) => !v && setReviewModal(null)}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-serif text-rose-950">Review: {reviewModal?.item?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Rating</Label>
              <div className="flex gap-1 mt-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}><Star className={`w-7 h-7 ${n <= rating ? "fill-amber-500 text-amber-500" : "text-rose-900/20"}`} /></button>)}</div>
            </div>
            <div><Label>Your Review</Label><Textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} className="mt-1.5" placeholder="Share your experience..." data-testid="review-text" /></div>
            <div><Label>Product Photo (mandatory)</Label>
              <label className="mt-1.5 flex items-center gap-2 border border-dashed border-rose-900/30 rounded-lg p-3 cursor-pointer text-sm text-muted-foreground" data-testid="review-photo-upload">
                <Upload className="w-4 h-4" />{photoUrl ? "Photo uploaded ✓" : "Upload a photo of the product"}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </label>
              {photoUrl && <img src={photoUrl} alt="" className="w-20 h-24 object-cover rounded mt-2" />}
            </div>
            <Button onClick={submitReview} disabled={busy || !photoUrl} className="w-full bg-rose-950 hover:bg-rose-900" data-testid="review-submit">{busy ? "Please wait..." : "Submit Review"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Modal */}
      <Dialog open={!!returnModal} onOpenChange={(v) => !v && setReturnModal(null)}>
        <DialogContent data-testid="return-dialog">
          <DialogHeader><DialogTitle className="font-serif text-rose-950">Return Order {returnModal?.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              ⚠️ Return allowed within <b>3 days of delivery</b>. A clear <b>unboxing video</b> showing the sealed packet, shipping label and all sides is <b>mandatory</b>. Product must be unused with tags intact.
            </div>
            <div><Label>Reason for Return</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" placeholder="e.g. size issue, defective, wrong product..." data-testid="return-reason" /></div>
            <div><Label>Unboxing Video (mandatory)</Label>
              <label className="mt-1.5 flex items-center gap-2 border border-dashed border-rose-900/30 rounded-lg p-3 cursor-pointer text-sm text-muted-foreground" data-testid="return-video-upload">
                <Upload className="w-4 h-4" />{videoUrl ? "Video uploaded ✓" : "Upload unboxing video"}
                <input type="file" accept="video/*" className="hidden" onChange={handleVideo} />
              </label>
              {busy && <p className="text-xs text-amber-700 mt-1">Uploading, please wait...</p>}
            </div>
            <Button onClick={submitReturn} disabled={busy || !videoUrl} className="w-full bg-rose-950 hover:bg-rose-900" data-testid="return-submit">{busy ? "Please wait..." : "Submit Return Request"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
